import { useTranslation } from "react-i18next";
import { formatDate } from "~/helpers/date";
import type { DocumentFiltersModel } from "~/hooks/useDocumentFilters";
import {
  FilterOperatorEnum,
  type FilterCondition,
  type FilterField,
  filterFields,
  filterIsDateByField,
} from "~/helpers/documentFilters";
import { ActiveFilterChips, type ActiveFilterChip } from "./ActiveFilterChips";

interface DocumentActiveFilterChipsProps {
  filters: DocumentFiltersModel;
}

export const DocumentActiveFilterChips = ({
  filters,
}: DocumentActiveFilterChipsProps) => {
  const { t } = useTranslation();
  const comparatorLabels: Record<FilterCondition["operator"], string> = {
    gte: t(($) => $.library.filters.isAfter),
    lte: t(($) => $.library.filters.isBefore),
    eq: t(($) => $.library.filters.equals),
    in: t(($) => $.library.filters.includes),
    isNull: t(($) => $.library.filters.equals),
  };
  const getComparatorLabel = (operators: FilterCondition["operator"][]) => {
    if (
      operators.length === 2 &&
      operators.includes(FilterOperatorEnum.gte) &&
      operators.includes(FilterOperatorEnum.lte)
    ) {
      return t(($) => $.library.filters.isBetween);
    }

    if (operators.length === 1) {
      const [operator] = operators;
      if (!operator) {
        throw new Error("Missing comparator operator");
      }
      return comparatorLabels[operator];
    }

    throw new Error(
      `Unsupported comparator operator combination: ${operators.join(",")}`,
    );
  };

  const formatFilterValue = (filter: FilterCondition) => {
    const { field } = filter;

    if (filter.operator === FilterOperatorEnum.isNull) {
      return t(($) => $.library.filters.notSet);
    }

    if (filterIsDateByField(field)) {
      if (typeof filter.value !== "string") return "";
      const date = new Date(filter.value);
      return Number.isNaN(date.getTime()) ? "" : formatDate(date);
    }

    const { labelsByValue } = filters.multiSelectFieldData[field];
    if (Array.isArray(filter.value)) {
      return filter.value
        .map((value) => labelsByValue[value] ?? value)
        .join(", ");
    }

    return labelsByValue[filter.value] ?? filter.value;
  };

  const filtersByField = filters.activeFilters.reduce<
    Partial<Record<FilterField, FilterCondition[]>>
  >((acc, filter) => {
    acc[filter.field] = [...(acc[filter.field] ?? []), filter];
    return acc;
  }, {});
  const activeFilterItems: ActiveFilterChip[] = filterFields.flatMap(
    (field) => {
      const fieldFilters = filtersByField[field] ?? [];
      const gteFilter = fieldFilters.find(
        (filter) => filter.operator === FilterOperatorEnum.gte,
      );
      const lteFilter = fieldFilters.find(
        (filter) => filter.operator === FilterOperatorEnum.lte,
      );

      if (gteFilter && lteFilter) {
        return [
          {
            key: `${field}-range`,
            label: t(($) => $.commons.document[field]),
            comparatorLabel: getComparatorLabel([
              FilterOperatorEnum.gte,
              FilterOperatorEnum.lte,
            ]),
            value: `${formatFilterValue(gteFilter)} - ${formatFilterValue(lteFilter)}`,
            onRemove: () => filters.removeFilter(field),
          },
        ];
      }

      return fieldFilters.map((filter) => ({
        key: `${field}-${filter.operator}`,
        label: t(($) => $.commons.document[field]),
        comparatorLabel: getComparatorLabel([filter.operator]),
        value: formatFilterValue(filter),
        onRemove: () => filters.removeFilter(field),
      }));
    },
  );

  return <ActiveFilterChips items={activeFilterItems} />;
};
